// Wire protocol shared by the server and the three pages.
// Control frames are JSON text; audio frames are binary: [FRAME_VERSION, ...PCM16 LE].

export const LANGS = ["ro", "es", "en"] as const;
export type Lang = (typeof LANGS)[number];
export const SOURCE_LANGS = ["ro", "es"] as const;
export type SourceLang = (typeof SOURCE_LANGS)[number];

/** Translation engines. Which ones are usable depends on the API keys the server has. */
export const PROVIDERS = ["openai", "gemini", "passthrough"] as const;
export type Provider = (typeof PROVIDERS)[number];
export function isProvider(v: unknown): v is Provider {
  return typeof v === "string" && (PROVIDERS as readonly string[]).includes(v);
}

export type Role = "broadcaster" | "operator" | "listener";
export type EventStatus = "idle" | "live" | "ended";

export const FRAME_VERSION = 0x01;
/** Broadcaster → server. */
export const INPUT_RATE = 16000;
/** Server → listener. */
export const OUTPUT_RATE = 24000;

export function isLang(v: unknown): v is Lang {
  return typeof v === "string" && (LANGS as readonly string[]).includes(v);
}
export function isSourceLang(v: unknown): v is SourceLang {
  return typeof v === "string" && (SOURCE_LANGS as readonly string[]).includes(v);
}

export type ChannelHealth = {
  state: "off" | "original" | "starting" | "running" | "error";
  /** ms since the pipeline last produced audio; null if never. */
  lastAudioAgoMs: number | null;
  restarts: number;
  lastError: string | null;
};

export type StateFrame = {
  type: "state";
  sourceLang: SourceLang;
  status: EventStatus;
  listeners: Record<Lang, number>;
  relayOriginal: boolean;
  // Operator-only extras:
  broadcasterConnected?: boolean;
  health?: Record<Lang, ChannelHealth>;
  costUsd?: number;
  /** Engine currently chosen for each target language. */
  engines?: Record<Lang, Provider>;
  /** Engines the operator may choose from. */
  availableEngines?: Provider[];
};

export type ClientFrame =
  | { type: "hello"; role: Role; eventCode: string; channel?: Lang; key?: string }
  | { type: "subscribe"; channel: Lang }
  | { type: "set_source_lang"; lang: SourceLang }
  | { type: "set_status"; status: EventStatus }
  | { type: "set_provider"; lang: Lang; provider: Provider }
  | { type: "ping" };

export type ServerFrame =
  | {
      type: "ready";
      eventId: string;
      eventName: string;
      channels: Lang[];
      sourceLang: SourceLang;
      status: EventStatus;
    }
  | StateFrame
  | { type: "error"; message: string; fatal?: boolean }
  | { type: "pong" };
